import { ViewHook } from "phoenix_live_view";
import "number-flow";

import { pageLocale } from "../utils/formatters";

const observers = new WeakMap<HTMLElement, IntersectionObserver>();

const RISE_LEAD_MS = 150;

const riseWait = (el: HTMLElement) => {
  const animation = el.closest<HTMLElement>("[data-report-rise]")?.getAnimations?.()[0];

  if (animation?.playState !== "running") {
    return 0;
  }

  const delay = animation.effect?.getTiming().delay ?? 0;

  return Math.max(0, delay + RISE_LEAD_MS - Number(animation.currentTime ?? 0));
};

export const startReportNumber = (el: HTMLElement) => {
  if (observers.has(el) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const { value, prefix, suffix, decimals } = el.dataset;
  const digits = Number(decimals);

  const flow = document.createElement("number-flow");
  flow.locales = pageLocale();
  flow.spinTiming = { duration: 1600, easing: "cubic-bezier(0.16, 1, 0.3, 1)" };
  flow.format = { minimumFractionDigits: digits, maximumFractionDigits: digits };
  flow.numberPrefix = prefix;
  flow.numberSuffix = suffix;
  flow.update(0);

  el.replaceChildren(flow);

  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      const wait = riseWait(el);

      if (wait > 0) {
        flow.style.visibility = "hidden";
        setTimeout(() => {
          flow.style.visibility = "";
          flow.update(Number(value));
        }, wait);
      } else {
        flow.update(Number(value));
      }
    } else {
      flow.animated = false;
      flow.update(0);
      flow.animated = true;
    }
  });

  observer.observe(el);
  observers.set(el, observer);
};

export const startEarlyReportNumbers = (root: ParentNode = document) => {
  root
    .querySelectorAll<HTMLElement>("[data-report-number-early]")
    .forEach((el) => startReportNumber(el));
};

class ReportNumber extends ViewHook {
  mounted() {
    startReportNumber(this.el);
  }

  destroyed() {
    observers.get(this.el)?.disconnect();
    observers.delete(this.el);
  }
}

export default ReportNumber;
